import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatClockDuration } from '../../../helpers/formatClockDuration'

export const MAX_RECORDING_SECONDS = 120

// Manages the screen-recording lifecycle: getDisplayMedia capture, the elapsed
// timer (auto-stops at MAX_RECORDING_SECONDS), and flushing the final blob.
const useScreenRecording = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recordingStopPromiseRef = useRef<Promise<Blob | null> | null>(null)
  const recordingStopResolveRef = useRef<((blob: Blob | null) => void) | null>(null)

  // Cleanup recording on unmount.
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: true,
      })
      mediaStreamRef.current = stream

      const chunks: BlobPart[] = []
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' })
        setRecordingBlob(blob)
        setIsRecording(false)
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
        recordingStopResolveRef.current?.(blob)
        recordingStopPromiseRef.current = null
        recordingStopResolveRef.current = null
      }

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') recorder.stop()
        stream.getTracks().forEach((t) => t.stop())
      }

      recorder.start()
      setIsRecording(true)
      setRecordingSeconds(0)

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          if (prev >= MAX_RECORDING_SECONDS - 1) {
            recorder.stop()
            stream.getTracks().forEach((t) => t.stop())
          }
          return prev + 1
        })
      }, 1000)
    } catch {
      // User cancelled
    }
  }, [])

  // Stops an in-progress recording and resolves with the final blob once the
  // recorder has flushed its last chunk (recorder.onstop).
  const stopRecordingAndGetBlob = useCallback((): Promise<Blob | null> => {
    if (recordingStopPromiseRef.current !== null) {
      return recordingStopPromiseRef.current
    }
    const recorder = mediaRecorderRef.current
    if (recorder?.state !== 'recording') {
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
      return Promise.resolve(null)
    }
    recordingStopPromiseRef.current = new Promise((resolve) => {
      recordingStopResolveRef.current = resolve
      recorder.stop()
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    })
    return recordingStopPromiseRef.current
  }, [])

  const stopRecording = useCallback(() => {
    void stopRecordingAndGetBlob()
  }, [stopRecordingAndGetBlob])

  const discardRecording = useCallback(() => setRecordingBlob(null), [])

  const resetRecording = useCallback(() => {
    setRecordingBlob(null)
    setRecordingSeconds(0)
  }, [])

  const formattedRecordingTime = useMemo(
    () => formatClockDuration(recordingSeconds),
    [recordingSeconds],
  )

  return {
    isRecording,
    recordingSeconds,
    recordingBlob,
    formattedRecordingTime,
    startRecording,
    stopRecording,
    stopRecordingAndGetBlob,
    discardRecording,
    resetRecording,
  }
}

export default useScreenRecording
